/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';

import {DEVICE_ORDER_ID} from './perf-total-component';
import {getAvailableFields} from './chart-options/chart-options-fields';
import {OgTranslateService} from '../filters/og-translate.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {BrowserLocationService} from '../../angular-platform-providers';
import {CHART_IMPORTS} from './chart-imports';

@Component({
  imports: CHART_IMPORTS,
  selector: 'adn-performance-totals',
  template: `
    <ul class="list-unstyled doubleColumned totalsTable">
      <li *ngFor="let kv of items">
        <span *ngIf="kv" class="titleField">
          <span *ngIf="kv.keyBase"><span class="fa fa-fw {{kv.baseIcon}}" *ngIf="kv.icon"></span><span class="fa fa-fw {{kv.icon}}" *ngIf="kv.icon"></span> {{kv.key}}</span>
          <span *ngIf="!kv.keyBase && resolvedTotals[kv.key] !== undefined"><span class="fa fa-fw {{kv.icon}}" *ngIf="kv.icon"></span>
            <span *ngIf="!hasCustomStatValue(kv.key, 'count')">{{keyed ? fieldTranslate(keyed + '.' + (kv.tKey || kv.key)) : kv.name}}</span><span *ngIf="hasCustomStatValue(kv.key, 'count')">{{(kv.tKey || kv.key)}} count<span *ngIf="hasCustomStatValue(kv.key, 'averageValue')"> | avg</span><span *ngIf="hasCustomStatValue(kv.key, 'totalValue')"> | total</span></span>
          </span>
        </span>
        <span *ngIf="kv" class="numericColumn">
          <span *ngIf="kv.keyBase">{{resolvedTotals[kv.keyBase][kv.key] | stat}}</span>
          <span *ngIf="!kv.keyBase && !hasCustomStatValue(kv.key, 'count')">{{resolvedTotals[useAlt ? (kv.altId || kv.key) : kv.key] | stat : kv.key}}</span>
          <span *ngIf="!kv.keyBase && hasCustomStatValue(kv.key, 'count')">{{resolvedTotals[useAlt ? (kv.altId || kv.key) : kv.key].count | stat : kv.key}}<span *ngIf="hasCustomStatValue(kv.key, 'averageValue')"> | {{resolvedTotals[kv.key].averageValue | stat : kv.key}}</span><span *ngIf="hasCustomStatValue(kv.key, 'totalValue')"> | {{resolvedTotals[kv.key].totalValue | stat : kv.key}}</span></span>
        </span>
      </li>
    </ul>
  `
})
export class PerformanceTotalsComponent implements OnChanges {
  @Input() chartType: string;
  @Input() keyed: string;
  @Input() orderBy: string;
  @Input() param: string;
  @Input() useAlt: boolean;
  @Input() totals: any;

  items: any[] = [];
  resolvedTotals: any = {};

  constructor(
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly translate: TranslateService,
    private readonly ogTranslate: OgTranslateService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.totals || changes.chartType || changes.param || changes.orderBy || changes.keyed || changes.useAlt) {
      this.rowsOrganiser();
    }
  }

  fieldTranslate(key: string): string {
    return this.ogTranslate.transform(key);
  }

  hasCustomStatValue(key: string, valueKey: string): boolean {
    return !_.isUndefined(_.get(this.resolvedTotals, [key, valueKey]));
  }

  private valIsEqual(val1: any, val2: any): boolean {
    const isMoney = _.get(val1, ['currency']);
    if (isMoney) {
      return _.get(val1, ['amount']) === _.get(val2, ['amount']) && _.get(val1, ['currency']) && _.get(val2, ['currency']);
    }
    return val1 === val2;
  }

  private rowsOrganiser(): void {
    if (!this.totals) {
      this.items = [];
      this.resolvedTotals = {};
      return;
    }

    const resolved = this.param ? this.totals[this.param] : this.totals;
    if (!resolved) {
      this.items = [];
      this.resolvedTotals = {};
      return;
    }
    this.resolvedTotals = resolved;

    let fields: any[];
    if (this.chartType) {
      const chartOptions = this.ChartOptionsUtil.getOptions(this.chartType).totalsFields;
      const selectableFields = _.keyBy(getAvailableFields(this.ChartOptionsUtil, this.localUserProfile, this.chartType, this.location), 'id');
      const chartOptionsFields = _.filter(chartOptions, (co: any) => selectableFields[co.id]);
      fields = _.filter(chartOptionsFields, (f: any) => f.id !== 'customFields');
    } else {
      fields = _.map(resolved, (d: any, k: string) => {
        return {
          name: this.keyed ? (this.translate.instant(this.keyed + '.' + k) || k) : k,
          id: k,
          key: k,
          orderBy: d
        };
      });
    }

    if (this.orderBy === 'VALUE') {
      fields = _.orderBy(fields, (s: any) => s.orderBy, 'desc');
    }

    let items: any[] = [];
    _.forEach(fields, (d: any) => {
      const id = _.get(d, 'id');
      d.orderId = DEVICE_ORDER_ID[id] || 1000;
      items.push(d);
    });
    items = _.orderBy(items, 'orderId');

    const customData: any[] = [];
    _.forEach(this.ChartOptionsUtil.getLineItemCustomFields(resolved), (f: string) => {
      customData.push({key: f, icon: 'fa-wrench'});
    });
    _.forEach(customData, (d: any) => {
      items.push(d);
    });

    const splitFeesData: any[] = [];
    _.forEach(this.ChartOptionsUtil.getSplitFees(resolved), (f: any) => {
      splitFeesData.push({key: f.name, keyBase: 'feeBreakdown', icon: 'fa-money', baseIcon: 'fa-long-arrow-right'});
    });
    let feesIndex = -1;
    for (let i = 0; i < items.length; i++) {
      if (items[i].key === 'fee') {
        feesIndex = i + 1;
        break;
      }
    }
    _.forEach(splitFeesData, (d: any) => {
      if (feesIndex > 0) {
        items.splice(feesIndex, 0, d);
      } else {
        items.push(d);
      }
    });

    this.items = _.filter(items, (item: any) => {
      const hideIfMatchedField = item.hideIfMatched;
      const fieldRef = item.keyBase ? [item.keyBase, item.key] : [item.key || item.id];
      const isUserLimited = item.user && item.user !== this.localUserProfile.get('username');
      return !isUserLimited && ((_.isFinite(_.get(resolved, fieldRef)) || _.get(resolved, fieldRef)) && (!hideIfMatchedField || !this.valIsEqual(_.get(resolved, fieldRef), resolved[hideIfMatchedField])));
    });
  }
}
